import { api } from "./api";
import { toast } from "sonner";
import { eventKey, mergeClassroomEvents, normalizeDashboardEvent, readDashboardStorage } from "./dashboardGadgets";

let syncQueue = Promise.resolve();
const pendingDeleteKey = "shadowGrovePendingClassroomDeletes";
const deletedHistoryKey = "shadowGroveDeletedClassroomEvents";
const readArray = (key) => {
    const value = readDashboardStorage(key, []);
    return Array.isArray(value) ? value : [];
};

export const schoolCalendarForChild = (calendar, childId) => Object.fromEntries(
    ["holidays", "vacations", "daysOff", "items"].map((key) => [key,
        (Array.isArray(calendar[key]) ? calendar[key] : []).filter((item) =>
            (key !== "items" || item.blocksSchool) &&
            (!item.child_id || String(item.child_id) === String(childId)) &&
            (!item.profileView || item.profileView === "school" || String(item.profileView) === String(childId))
        ),
    ])
);

export function queueSchoolCalendarSync(childIds) {
    const calendar = readDashboardStorage("shadowGroveSchoolCalendar", {});
    syncQueue = syncQueue.then(async () => {
        for (const id of childIds) {
            await api.post(`/children/${encodeURIComponent(id)}/classroom-dashboard/sync`, { calendar: schoolCalendarForChild(calendar, id), time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone });
        }
    }).catch(() => toast.error("School calendar sync failed. Open the dashboard to retry."));
}

export function queueAttendanceSync(childId, records) {
    syncQueue = syncQueue.then(async () => {
        await api.post(`/children/${encodeURIComponent(childId)}/attendance/sync`, { records });
    }).catch(() => toast.error("Attendance was saved here, but report sync failed. Open Reports to retry."));
    window.dispatchEvent(new CustomEvent("shadowGroveAttendanceUpdated", { detail: { childId } }));
}

export function queueGradingSettingsSync(childId, subjectId, settings) {
    syncQueue = syncQueue.then(async () => {
        await api.post(`/children/${encodeURIComponent(childId)}/classroom-dashboard/sync`, { grading_settings: { [subjectId]: settings } });
    }).catch(() => toast.error("Grading settings could not synchronize with Reports."));
}

export async function flushClassroomDeletions(childIds) {
    const allowed = new Set(childIds.map(String));
    for (const event of readArray(pendingDeleteKey).filter((item) => allowed.has(String(item.child_id)))) {
        try {
            await api.delete(`/children/${encodeURIComponent(event.child_id)}/classroom-dashboard/events/${encodeURIComponent(event.event_id ?? event.id)}`);
        } catch (error) {
            if (error?.response?.status !== 404) throw error;
        }
        localStorage.setItem(pendingDeleteKey, JSON.stringify(readArray(pendingDeleteKey).filter((item) => eventKey(item) !== eventKey(event))));
    }
}

// Keep the classroom/calendar's synchronous storage contract while publishing
// each content change to the shared store in order, even when the board is closed.
export function saveClassroomEvents(nextEvents, scope = null) {
    const previous = readArray("shadowGroveEvents");
    if (scope) {
        const belongs = (raw) => {
            const event = normalizeDashboardEvent(raw);
            return event.child_id === String(scope.childId) && event.subject_id === String(scope.subjectId);
        };
        // A classroom snapshot may predate another class's latest save.
        nextEvents = [...previous.filter((event) => !belongs(event)), ...nextEvents.filter(belongs)];
    }
    const byId = new Map(previous.map((event) => [eventKey(event), event]));
    const now = new Date().toISOString();
    const changed = [];
    const saved = nextEvents.map(normalizeDashboardEvent).map((incoming) => {
        const prior = byId.get(eventKey(incoming));
        if (JSON.stringify(prior) === JSON.stringify(incoming)) return incoming;
        const event = { ...incoming, updated_at: now };
        if (prior) {
            const existing = normalizeDashboardEvent(prior);
            event.student_progress = existing.student_progress;
            event.postponement_requests = existing.postponement_requests;
            for (const key of ["parent_review", "academic_grade_level", "report_archived_through", "follow_up", "excused"]) event[key] = existing[key];
            event.data = { ...event.data, follow_up: existing.follow_up, excused: existing.excused, student_progress: existing.student_progress, postponement_requests: existing.postponement_requests,
                parent_review: existing.parent_review, academic_grade_level: existing.academic_grade_level, report_archived_through: existing.report_archived_through };
        }
        if ((event.event_id ?? event.id) != null && event.child_id && event.subject_id) changed.push(event);
        return event;
    });
    const retainedIds = new Set(saved.map(eventKey));
    const removed = previous.filter((event) => !retainedIds.has(eventKey(event)) && event.child_id && (event.event_id ?? event.id) != null);
    const history = new Map([...readArray(deletedHistoryKey), ...removed].map((event) => [eventKey(event), event]));
    const restored = saved.filter((event) => !byId.has(eventKey(event)) && history.has(eventKey(event)));
    restored.forEach((event) => history.delete(eventKey(event)));
    localStorage.setItem(deletedHistoryKey, JSON.stringify([...history.values()]));
    const pending = new Map([...readArray(pendingDeleteKey), ...removed].filter((event) => !retainedIds.has(eventKey(event))).map((event) => [eventKey(event), event]));
    localStorage.setItem(pendingDeleteKey, JSON.stringify([...pending.values()]));
    localStorage.setItem("shadowGroveEvents", JSON.stringify(saved));
    window.dispatchEvent(new CustomEvent("shadowGroveEventsUpdated", { detail: saved }));
    const changesByChild = new Map();
    changed.forEach((event) => {
        const id = String(event.child_id);
        if (!changesByChild.has(id)) changesByChild.set(id, []);
        changesByChild.get(id).push(event);
    });
    const childIds = [...new Set([...changed, ...removed].map((event) => String(event.child_id)))];
    syncQueue = syncQueue.then(async () => {
        for (const [id, events] of changesByChild) {
            for (let offset = 0; offset < events.length; offset += 1000) {
                const response = await api.post(`/children/${encodeURIComponent(id)}/classroom-dashboard/sync`, {
                    events: events.slice(offset, offset + 1000),
                    restore_event_ids: restored.filter((event) => String(event.child_id) === id).map((event) => String(event.event_id ?? event.id)),
                });
                if (response.data?.rejected_events?.length) toast.error("Some assignments could not sync because their classroom IDs no longer match. Open the dashboard to retry matching each class.");
                if (Array.isArray(response.data?.events)) {
                    const sent = new Set(events.slice(offset, offset + 1000).map(eventKey));
                    const returned = new Map(response.data.events.filter((event) => sent.has(eventKey(event))).map((event) => [eventKey(event), event]));
                    const fresh = readArray("shadowGroveEvents").map((event) => {
                        const remote = returned.get(eventKey(event));
                        return remote ? mergeClassroomEvents([event], [remote], id)[0] : event;
                    });
                    localStorage.setItem("shadowGroveEvents", JSON.stringify(fresh));
                    window.dispatchEvent(new CustomEvent("shadowGroveEventsUpdated", { detail: fresh }));
                }
            }
        }
        await flushClassroomDeletions(childIds);
    }).catch(() => toast.error("Saved in this browser, but classroom sync failed. Open the dashboard to retry."));
    return saved;
}

export const waitForClassroomSync = () => syncQueue;
